import { RegSettingDetail } from 'src/settings/models/regSetting';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { getDashboardSettings, updateDashboardSettings } from 'src/shared/services/regSettingService';
import { DashboardView } from './types';

const CATEGORY = 'dashboard';
const DEFAULT_KEY = 'views';

export type ViewScope = {
  associationId?: number;
  districtId?: number;
};

const scopeKeyName = (scope: ViewScope): string => {
  if (scope.associationId) return `${DEFAULT_KEY}:association:${scope.associationId}`;
  if (scope.districtId) return `${DEFAULT_KEY}:district:${scope.districtId}`;
  return DEFAULT_KEY;
};

const emptyDetail = (keyName: string): RegSettingDetail => ({
  settings_id: 0,
  product_id: 0,
  category: CATEGORY,
  keyName,
  field: 'text',
  varchar: null,
  integer: 0,
  smallint: 0,
  bit: null,
  text: null,
});

export type LoadResult = {
  views: DashboardView[] | null;
  settingsId: number;
  /** True om den hämtade vyn kom från fallback (default) istället för scope-specifik */
  fromFallback: boolean;
};

export const loadDashboardViews = async (scope: ViewScope): Promise<LoadResult> => {
  const keyName = scopeKeyName(scope);
  try {
    const res = await getDashboardSettings();
    const scopeSetting = res.data.find((s) => s.keyName === keyName);
    const defaultSetting = res.data.find((s) => s.keyName === DEFAULT_KEY);
    const setting = scopeSetting?.text ? scopeSetting : defaultSetting;
    if (!setting?.text) {
      return { views: null, settingsId: scopeSetting?.settings_id ?? 0, fromFallback: !scopeSetting?.text };
    }
    const parsed = JSON.parse(setting.text) as DashboardView[];
    if (!Array.isArray(parsed)) {
      return { views: null, settingsId: scopeSetting?.settings_id ?? 0, fromFallback: setting !== scopeSetting };
    }
    return { views: parsed, settingsId: scopeSetting?.settings_id ?? 0, fromFallback: setting !== scopeSetting };
  } catch (e) {
    ConsoleHelper.error(e);
    return { views: null, settingsId: 0, fromFallback: false };
  }
};

export const saveDashboardViews = async (views: DashboardView[], settingsId: number, scope: ViewScope): Promise<number> => {
  const keyName = scopeKeyName(scope);
  const detail: RegSettingDetail = {
    ...emptyDetail(keyName),
    settings_id: settingsId,
    text: JSON.stringify(views),
  };
  try {
    await updateDashboardSettings([detail]);
    // Vid INSERT (settings_id=0) behöver vi hämta nytt id för efterföljande PUT
    if (settingsId === 0) {
      const res = await getDashboardSettings();
      return res.data.find((s) => s.keyName === keyName)?.settings_id ?? 0;
    }
    return settingsId;
  } catch (e) {
    ConsoleHelper.error(e);
    throw e;
  }
};
